import { useNotificationSound } from "../../../utilities/audio/notificationSounds";
import React, { useState, useEffect, ReactNode, useRef, useCallback } from "react";
import { useAuth } from "../../../firebase/AuthProvider";
import { Conversation, getMessageContext, markChatActivityForMessageRead, markChatReadThrough, Message, toggleChatBookmark } from "../../../services/chatService/ChatService";
import ChatHeader from "./ChatHeader";
import MessageList from "./MessageList";
import EmojiPicker from "../emojiPicker/EmojiPicker";
import { conversationRead$, useGlobalChat } from "../../../hooks/globalChatHooks/useGlobalChat";
import "./GlobalChatInput.css";
import { ValidateOtpResponseDto } from "../../../interfaces/ExtermalUserDtos/ExternalUsersDtos";

import { formatFileSize } from "../../../utilities/fileUtils";

import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";

import ResourceLinkPreviews from '../resourceChat/ResourceLinkPreviews';
import TaskFaqLinkPreviews from '../resourceChat/TaskFaqLinkPreviews';
import MessageEditor, { MessageEditorRef } from "./MessageEditor";
import Tooltip from "../../ui/Tooltip/Tooltip";
import ThreadPanel from "./ThreadPanel";
import ChatSearchPanel from "./ChatSearchPanel";
import { isAutomatedBotConversation } from "../../../utilities/chat/conversationUtils";
import ChatMessageSkeleton from "./ChatMessageSkeleton";

import { Send, Smile, Paperclip, File as FileIcon, Loader2, X, Check, LockKeyhole, Plus, Type, MessageCircleMore, Sparkles } from "lucide-react";
import { MAX_FILE_SIZE_KB, MAX_FILE_SIZE_MB } from "../../../Data/SD";
import { blurFocusedChatControl, dismissTopChatEscapeLayer } from "../../../utilities/chat/chatEscapeLayers";
import { clearChatDraft, getChatDraft, nextChatDraftRevision, savePendingChatDraft } from "../../../services/chatService/ChatDrafts";

interface GlobalChatWindowProps {
    conversation: Conversation;
    handleChatClose?: () => void;
    singlePane?: boolean;
    isShowCallingIcon?: boolean;
    externalUserDetails?: ValidateOtpResponseDto | null;
    scrollToBottomTrigger?: boolean;
    isSideView?: boolean;
    isActiveView?: boolean;
    initialThreadRootId?: number;
    focusedMessageId?: number;
    onClearLinkedTarget?: () => void;
    appearance?: "default" | "meeting-dark";
}

const GlobalChatWindow: React.FC<GlobalChatWindowProps> = ({
    conversation,
    handleChatClose,
    singlePane = true,
    isShowCallingIcon = true,
    externalUserDetails = null,
    scrollToBottomTrigger,
    isSideView = false,
    isActiveView = true,
    initialThreadRootId,
    focusedMessageId,
    onClearLinkedTarget,
    appearance = "default"
}) => {
    const isMeetingDark = appearance === "meeting-dark";
    const activeViewRef = useRef(isActiveView);
    activeViewRef.current = isActiveView;
    const { currentUser } = useAuth();
    void (useNavigate());

    const chatInputRef = useRef<MessageEditorRef>(null);
    const [commentText, setCommentText] = useState("");
    const [commentDoc, setCommentDoc] = useState<any>(null);
    const [isEditorEmpty, setIsEditorEmpty] = useState(true);
    const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
    const [activeThreadMessage, setActiveThreadMessage] = useState<Message | null>(null);
    const [utilityPanel, setUtilityPanel] = useState<"search" | "bookmarks" | null>(null);
    const [internalFocusedMessageId, setInternalFocusedMessageId] = useState<number | undefined>();
    const dismissedLinkedThreadRef = useRef<number | null>(null);
    const draftSaveTimerRef = useRef<number | null>(null);
    const pendingDraftRef = useRef<{ plaintext: string; doc: unknown; updatedAt: number } | null>(null);
    const threadOriginFocusRef = useRef<HTMLElement | null>(null);
    const draftBeforeEditRef = useRef<{ plaintext: string; doc: unknown } | null>(null);

    const isChannel = Boolean(conversation.isChannel || conversation.channel);
    const isChannelArchived = Boolean(conversation.channel?.isArchived);
    const currentViewerId = externalUserDetails?.externalUserDetailId ?? currentUser?.uid;
    const isBotConversation = isAutomatedBotConversation(conversation, currentViewerId);
    // Fail closed for channels: writing is enabled only when the API explicitly
    // confirms effective write permission for the authenticated user.
    const canWriteToConversation =
        (!isChannel && !isBotConversation) ||
        (conversation.channel?.canWrite === true && !isChannelArchived);

    const readOnlyTitle = isBotConversation
        ? "Automated notifications"
        : isChannelArchived
        ? "Archived channel"
        : "Read-only channel";

    const readOnlyReason = isBotConversation
        ? "Waivera sends automated updates here. Replies are disabled."
        : isChannelArchived
        ? "This channel has been archived and can no longer be written to. You can still review its existing messages."
        : "You have read-only access to this channel. Contact a channel administrator if you need permission to post.";

    const {
        messages,
        setMessages,
        isLoading,
        sendMessage,
        retryMessage,
        cancelMessage,
        sendReaction,
        typingUsers,
        sendTypingStatus,
        hasMore,
        loadingMore,
        loadMoreMessages,
    } = useGlobalChat(conversation.id, externalUserDetails, isActiveView);

    const effectiveFocusedMessageId = internalFocusedMessageId ?? focusedMessageId;

    useEffect(() => {
        if (!currentViewerId) return;
        const draft = getChatDraft(currentViewerId, conversation.id);
        setCommentText(draft?.plaintext ?? "");
        setCommentDoc(draft?.doc ?? null);
        setIsEditorEmpty(!draft?.plaintext.trim());
        const frame = window.requestAnimationFrame(() => {
            const latest = getChatDraft(currentViewerId, conversation.id);
            setCommentText(latest?.plaintext ?? ''); setCommentDoc(latest?.doc ?? null); setIsEditorEmpty(!latest?.plaintext.trim());
            chatInputRef.current?.setContent(latest?.doc ?? latest?.plaintext ?? '', { focus: activeViewRef.current, emitUpdate: false });
        });
        return () => window.cancelAnimationFrame(frame);
    }, [conversation.id, currentViewerId]);

    useEffect(() => () => {
        if (draftSaveTimerRef.current) window.clearTimeout(draftSaveTimerRef.current);
        if (currentViewerId && pendingDraftRef.current) {
            savePendingChatDraft({
                userId: currentViewerId,
                conversationId: conversation.id,
                ...pendingDraftRef.current,
            });
        }
        pendingDraftRef.current = null;
    }, [conversation.id, currentViewerId]);

    useEffect(() => {
        const flushDraft = (event: Event) => {
            const detail = (event as CustomEvent).detail;
            if (!currentViewerId || detail?.userId !== currentViewerId || detail?.conversationId !== conversation.id) return;
            if (editingMessageId !== null || activeThreadMessage) {
                detail.blockedReason = editingMessageId !== null
                    ? 'Finish or cancel your message edit before preparing this resource.'
                    : 'Close the open thread before preparing this resource.';
                return;
            }
            if (!pendingDraftRef.current) return;
            if (draftSaveTimerRef.current) window.clearTimeout(draftSaveTimerRef.current);
            if (!savePendingChatDraft({ userId: currentViewerId, conversationId: conversation.id, ...pendingDraftRef.current })) {
                detail.failed = true;
                return;
            }
            pendingDraftRef.current = null;
        };
        window.addEventListener('chat-draft-flush', flushDraft);
        return () => window.removeEventListener('chat-draft-flush', flushDraft);
    }, [conversation.id, currentViewerId, editingMessageId, activeThreadMessage]);

    useEffect(() => {
        const refreshSharedDraft = (event: Event) => {
            if (!currentViewerId) return;
            const detail = (event as CustomEvent).detail;
            if (detail?.userId !== currentViewerId || detail?.conversationId !== conversation.id || editingMessageId !== null || activeThreadMessage) return;
            if (draftSaveTimerRef.current) window.clearTimeout(draftSaveTimerRef.current);
            const draft = getChatDraft(currentViewerId, conversation.id);
            if (!draft) return;
            pendingDraftRef.current = null;
            setCommentText(draft.plaintext); setCommentDoc(draft.doc); setIsEditorEmpty(!draft.plaintext.trim());
            chatInputRef.current?.setContent(draft.doc ?? draft.plaintext, { focus: false, emitUpdate: false });
        };
        window.addEventListener('resource-chat-draft', refreshSharedDraft);
        return () => window.removeEventListener('resource-chat-draft', refreshSharedDraft);
    }, [conversation.id, currentViewerId, editingMessageId, activeThreadMessage]);

    useEffect(() => {
        setUtilityPanel(null);
        setInternalFocusedMessageId(undefined);
    }, [conversation.id]);

    const handleToggleBookmark = async (messageId: number): Promise<boolean> => {
        if (!currentUser) throw new Error("Sign in to save messages.");
        const previousMessage = messages.find(message => message.id === messageId)
            ?? (activeThreadMessage?.id === messageId ? activeThreadMessage : undefined);
        const previousValue = Boolean(previousMessage?.isBookmarked);
        const optimisticValue = !previousValue;
        setMessages(previous => previous.map(message => message.id === messageId ? { ...message, isBookmarked: optimisticValue } : message));
        setActiveThreadMessage(previous => previous?.id === messageId ? { ...previous, isBookmarked: optimisticValue } : previous);
        try {
            const isBookmarked = await toggleChatBookmark(await currentUser.getIdToken(), messageId);
            setMessages(previous => previous.map(message => message.id === messageId ? { ...message, isBookmarked } : message));
            setActiveThreadMessage(previous => previous?.id === messageId ? { ...previous, isBookmarked } : previous);
            toast.success(isBookmarked ? "Message saved." : "Message removed from saved.", { autoClose: 1600 });
            return isBookmarked;
        } catch (error: any) {
            setMessages(previous => previous.map(message => message.id === messageId ? { ...message, isBookmarked: previousValue } : message));
            setActiveThreadMessage(previous => previous?.id === messageId ? { ...previous, isBookmarked: previousValue } : previous);
            toast.error(error?.message || "Unable to update the saved message.");
            throw error;
        }
    };

    const handleOpenSearchResult = async (result: Message) => {
        if (!currentUser) return;
        setUtilityPanel(null);
        setInternalFocusedMessageId(result.id);
        try {
            const token = await currentUser.getIdToken();
            if (result.threadRootMessageId) {
                let rootMessage = messages.find(message => message.id === result.threadRootMessageId);
                if (!rootMessage) {
                    const rootContext = await getMessageContext(token, conversation.id, result.threadRootMessageId, 0, 0);
                    rootMessage = rootContext.find(message => message.id === result.threadRootMessageId);
                }
                if (rootMessage) setActiveThreadMessage(rootMessage);
                return;
            }

            setActiveThreadMessage(null);
            const context = messages.some(message => message.id === result.id)
                ? []
                : await getMessageContext(token, conversation.id, result.id);
            if (context.length > 0) {
                setMessages(previous => {
                    const byId = new Map([...previous, ...context].map(message => [message.id, message]));
                    return Array.from(byId.values()).sort((left, right) => new Date(left.createDate).getTime() - new Date(right.createDate).getTime());
                });
            }
        } catch (error) {
            console.error("Unable to open the search result", error);
            toast.error("Unable to open that message.");
        }
    };

    const playSendSound = useNotificationSound("messageSent");
    const isSendingRef = useRef(false);
    void (useState(false));

    const queuedReadTargetRef = useRef<{ messageId: number; threadRootMessageId?: number } | null>(null);
    const processingReadTargetRef = useRef(false);
    const lastReadTargetRef = useRef<string>("");

    useEffect(() => {
        lastReadTargetRef.current = "";
    }, [isActiveView, activeThreadMessage?.id, utilityPanel]);

    useEffect(() => {
        queuedReadTargetRef.current = null;
        processingReadTargetRef.current = false;
        lastReadTargetRef.current = "";
    }, [conversation.id]);

    const handleReadThrough = useCallback((messageId: number, threadRootMessageId?: number) => {
        if (!currentUser || !isActiveView || messageId <= 0) return;
        const targetKey = `${threadRootMessageId ?? 0}:${messageId}`;
        if (lastReadTargetRef.current === targetKey) return;
        queuedReadTargetRef.current = { messageId, threadRootMessageId };
        if (processingReadTargetRef.current) return;

        const processQueuedRead = async () => {
            processingReadTargetRef.current = true;
            try {
                while (queuedReadTargetRef.current) {
                    const target = queuedReadTargetRef.current;
                    queuedReadTargetRef.current = null;
                    const state = await markChatReadThrough(
                        await currentUser.getIdToken(),
                        conversation.id,
                        target.messageId,
                        target.threadRootMessageId,
                    );
                    lastReadTargetRef.current = `${target.threadRootMessageId ?? 0}:${target.messageId}`;
                    if (!target.threadRootMessageId) conversationRead$.next(conversation.id);
                    window.dispatchEvent(new CustomEvent("chat-activity-read", {
                        detail: {
                            conversationId: conversation.id,
                            readActivityIds: state.readActivityIds,
                            lastVisibleMessageId: state.lastVisibleMessageId,
                            threadRootMessageId: state.threadRootMessageId,
                        },
                    }));
                }
            } catch (error) {
                console.error("Unable to update visible chat read state", error);
            } finally {
                processingReadTargetRef.current = false;
            }
        };
        void processQueuedRead();
    }, [conversation.id, currentUser, isActiveView]);

    useEffect(() => {
        if (!focusedMessageId || !currentUser || !isActiveView) return;
        let cancelled = false;
        const markLinkedActivityRead = async () => {
            try {
                await markChatActivityForMessageRead(await currentUser.getIdToken(), focusedMessageId);
                if (!cancelled) {
                    window.dispatchEvent(new CustomEvent("chat-activity-read", { detail: { messageId: focusedMessageId } }));
                }
            } catch (error) {
                console.error("Unable to mark linked Chat activity as read", error);
            }
        };
        void markLinkedActivityRead();
        return () => { cancelled = true; };
    }, [currentUser, focusedMessageId, isActiveView]);

    useEffect(() => {
        if (!initialThreadRootId ||
            activeThreadMessage?.id === initialThreadRootId ||
            dismissedLinkedThreadRef.current === initialThreadRootId) return;
        const threadRoot = messages.find(message => message.id === initialThreadRootId);
        if (threadRoot) {
            setActiveThreadMessage(threadRoot);
            return;
        }

        let cancelled = false;
        const loadLinkedThread = async () => {
            if (!currentUser) return;
            try {
                const context = await getMessageContext(await currentUser.getIdToken(), conversation.id, initialThreadRootId, 0, 0);
                const linkedRoot = context.find(message => message.id === initialThreadRootId);
                if (!cancelled && linkedRoot) setActiveThreadMessage(linkedRoot);
            } catch (error) {
                console.error("Unable to open the linked thread", error);
            }
        };
        void loadLinkedThread();
        return () => { cancelled = true; };
    }, [activeThreadMessage?.id, conversation.id, currentUser, initialThreadRootId, messages]);

    useEffect(() => {
        dismissedLinkedThreadRef.current = null;
    }, [conversation.id, focusedMessageId, initialThreadRootId]);

    const closeActiveThread = useCallback(() => {
        if (initialThreadRootId) dismissedLinkedThreadRef.current = initialThreadRootId;
        setActiveThreadMessage(null);
        setInternalFocusedMessageId(undefined);
        onClearLinkedTarget?.();
        const focusTarget = threadOriginFocusRef.current;
        threadOriginFocusRef.current = null;
        if (focusTarget?.isConnected) window.requestAnimationFrame(() => focusTarget.focus());
    }, [initialThreadRootId, onClearLinkedTarget]);

    useEffect(() => {
        if (initialThreadRootId || !focusedMessageId || !currentUser) return;
        let cancelled = false;
        const loadLinkedMessage = async () => {
            try {
                const token = await currentUser.getIdToken();
                let targetMessage = messages.find(message => message.id === focusedMessageId);
                let context: Message[] = [];

                if (!targetMessage) {
                    context = await getMessageContext(token, conversation.id, focusedMessageId);
                    targetMessage = context.find(message => message.id === focusedMessageId);
                }
                if (cancelled) return;

                if (context.length > 0) {
                    setMessages(previous => {
                        const byId = new Map([...previous, ...context].map(message => [message.id, message]));
                        return Array.from(byId.values()).sort((a, b) => new Date(a.createDate).getTime() - new Date(b.createDate).getTime());
                    });
                }

                // Activity records created before thread-root persistence may only
                // carry the reply ID. Infer and open the owning thread from the
                // resolved message instead of trying to highlight a hidden reply
                // in the main conversation timeline.
                const inferredThreadRootId = targetMessage?.threadRootMessageId;
                if (inferredThreadRootId && activeThreadMessage?.id !== inferredThreadRootId) {
                    let rootMessage = messages.find(message => message.id === inferredThreadRootId)
                        ?? context.find(message => message.id === inferredThreadRootId);
                    if (!rootMessage) {
                        const rootContext = await getMessageContext(token, conversation.id, inferredThreadRootId, 0, 0);
                        rootMessage = rootContext.find(message => message.id === inferredThreadRootId);
                    }
                    if (!cancelled && rootMessage) setActiveThreadMessage(rootMessage);
                }
            } catch (error) {
                console.error("Unable to open the linked message", error);
            }
        };
        void loadLinkedMessage();
        return () => { cancelled = true; };
    }, [activeThreadMessage?.id, conversation.id, currentUser, focusedMessageId, initialThreadRootId, messages, setMessages]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!isActiveView) return;
            if (event.defaultPrevented || dismissTopChatEscapeLayer(event)) return;
            // TipTap suggestions own Escape while their menu is visible. Let the
            // editor dismiss the suggestion before the thread or chat closes.
            if (event.key === 'Escape' && document.querySelector('.tippy-box[data-state="visible"]')) return;
            if (event.key === 'Escape' && utilityPanel) {
                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation();
                setUtilityPanel(null);
                return;
            }
            if (blurFocusedChatControl(event)) return;
            if (event.key === 'Escape' && activeThreadMessage) {
                // Both handlers live on window. stopPropagation alone does not
                // prevent another listener on the same EventTarget.
                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation();
                closeActiveThread();
            }
        };

        // Use capture: true to ensure this component intercepts the key before the Page
        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [activeThreadMessage, initialThreadRootId, isActiveView, onClearLinkedTarget, utilityPanel, closeActiveThread]);

    const [selectedFile, setSelectedFile] = useState<File>();
    const [filePreview, setFilePreview] = useState<{ url: string; icon?: ReactNode; } | null>(null);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showComposerActions, setShowComposerActions] = useState(false);
    const [showFormatting, setShowFormatting] = useState(false);
    useEffect(() => {
        const checkShare = (event: Event) => {
            const detail = (event as CustomEvent).detail;
            if (!currentViewerId || detail?.userId !== currentViewerId) return;
            if (editingMessageId !== null) detail.blockedReason = 'Finish or cancel your message edit before sharing this resource.';
            else if (selectedFile) detail.blockedReason = 'Send or remove your pending attachment before sharing this resource.';
            else if (activeThreadMessage) detail.blockedReason = 'Close the open thread before sharing this resource.';
        };
        window.addEventListener('chat-resource-share-check', checkShare);
        return () => window.removeEventListener('chat-resource-share-check', checkShare);
    }, [currentViewerId, editingMessageId, selectedFile, activeThreadMessage]);

    const [isSending, setIsSending] = useState(false);

    useEffect(() => {
        if (!isActiveView) {
            setShowEmojiPicker(false);
            setShowComposerActions(false);
        }
    }, [isActiveView]);

    const clearSelectedFile = () => {
        if (filePreview?.url) URL.revokeObjectURL(filePreview.url);
        setSelectedFile(undefined);
        setFilePreview(null);
    };

    const selectFile = (file: File): boolean => {
        if (editingMessageId !== null) {
            toast.info("Files cannot be attached while editing a message.");
            return false;
        }

        if (file.size / 1024 > MAX_FILE_SIZE_KB) {
            toast.warn(`File too large. Maximum file size is ${MAX_FILE_SIZE_MB} MB.`);
            return false;
        }

        if (filePreview?.url) URL.revokeObjectURL(filePreview.url);
        setSelectedFile(file);
        setFilePreview({ url: URL.createObjectURL(file) });
        return true;
    };

    const otherParticipants = conversation.participants.filter(
        (p) => p.id !== currentViewerId
    );
    const composerTargetLabel = (() => {
        const rawLabel = conversation.channel?.name ||
            (otherParticipants.length === 1 ? otherParticipants[0]?.displayName : '') ||
            (otherParticipants.length > 1 ? 'group' : 'conversation');
        return rawLabel.length > 24 ? `${rawLabel.slice(0, 23)}…` : rawLabel;
    })();
    const composerPlaceholder = `Message ${composerTargetLabel}…`;
    const typingDisplayNames = typingUsers
        .map((id) => otherParticipants.find((p) => p.id === id)?.displayName)
        .filter(Boolean);

    const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleEditorUpdate = (text: string, doc: any, isEmpty: boolean = false) => {
        if (!canWriteToConversation) return;
        setCommentText(text);
        setCommentDoc(doc);
        setIsEditorEmpty(isEmpty);

        if (currentViewerId && editingMessageId === null) {
            const pendingDraft = { plaintext: text, doc, updatedAt: nextChatDraftRevision() };
            pendingDraftRef.current = pendingDraft;
            if (draftSaveTimerRef.current) window.clearTimeout(draftSaveTimerRef.current);
            draftSaveTimerRef.current = window.setTimeout(() => {
                if (savePendingChatDraft({ userId: currentViewerId, conversationId: conversation.id, ...pendingDraft })) pendingDraftRef.current = null;
                draftSaveTimerRef.current = null;
            }, 250);
        }

        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        else sendTypingStatus(true);

        typingTimeoutRef.current = setTimeout(() => {
            sendTypingStatus(false);
            typingTimeoutRef.current = null;
        }, 3000);
    };

    const restoreComposerDraftAfterEdit = () => {
        const draft = draftBeforeEditRef.current;
        draftBeforeEditRef.current = null;
        setEditingMessageId(null);
        setCommentText(draft?.plaintext ?? "");
        setCommentDoc(draft?.doc ?? null);
        setIsEditorEmpty(!draft?.plaintext.trim());
        window.requestAnimationFrame(() => {
            if (draft?.doc) chatInputRef.current?.setContent(draft.doc);
            else if (draft?.plaintext) chatInputRef.current?.setContent(draft.plaintext);
            else chatInputRef.current?.clearContent();
        });
    };

    const cancelEdit = restoreComposerDraftAfterEdit;

    const handleSubmit = async () => {
        if (!canWriteToConversation) {
            toast.info(readOnlyReason);
            return;
        }
        if ((isEditorEmpty && !selectedFile) || (editingMessageId !== null && isSendingRef.current)) return;

        // Capture current values for the API call
        const textToSend = commentText;
        const docToSend = commentDoc;
        const fileToSend = selectedFile;
        const isEditing = editingMessageId;

        if (isEditing) {
            isSendingRef.current = true;
            setIsSending(true);
        }

        // IMMEDIATE CLEAR: Let user type next message instantly
        setCommentText("");
        setCommentDoc(null);
        if (filePreview?.url) URL.revokeObjectURL(filePreview.url);
        setSelectedFile(undefined);
        setFilePreview(null);
        chatInputRef.current?.clearContent();
        if (isEditing) restoreComposerDraftAfterEdit();
        if (!isEditing && currentViewerId) {
            if (draftSaveTimerRef.current) window.clearTimeout(draftSaveTimerRef.current);
            draftSaveTimerRef.current = null;
            pendingDraftRef.current = null;
            clearChatDraft(currentViewerId, conversation.id);
        }

        try {
            const trimTrailingEmptyNodes = (doc: any) => {
                if (!doc || !doc.content || !Array.isArray(doc.content)) return doc;
                const newContent = [...doc.content];
                while (newContent.length > 0) {
                    const lastNode = newContent[newContent.length - 1];
                    if (lastNode.type === 'paragraph' && (!lastNode.content || lastNode.content.length === 0)) {
                        newContent.pop();
                    } else break;
                }
                return { ...doc, content: newContent };
            };

            const cleanDoc = docToSend ? trimTrailingEmptyNodes(docToSend) : null;

            if (isEditing) {
                setMessages(previous => previous.map(message => message.id === isEditing ? {
                    ...message,
                    messageDoc: cleanDoc ? { v: 1, editor: "tiptap", schema: "pm", doc: cleanDoc } : null,
                    messageContent: textToSend,
                    messagePlaintext: textToSend,
                    isEdited: true,
                } : message));
                const token = await currentUser?.getIdToken();
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/chat/message/${isEditing}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({ newContent: textToSend, doc: cleanDoc })
                });
                if (!res.ok) throw new Error("Failed to update message");
            } else {
                playSendSound();
                void sendMessage(textToSend, cleanDoc, fileToSend);
            }
        } catch (error: any) {
            toast.error(error.message || "Action failed.");
            if (isEditing) {
                const failedMessage = messages.find(message => message.id === isEditing);
                if (failedMessage) {
                    setMessages(previous => previous.map(message => message.id === isEditing ? failedMessage : message));
                }
            }
        } finally {
            if (isEditing) {
                isSendingRef.current = false;
                setIsSending(false);
            }
        }
    };

    const handleQuickStart = async (message: string) => {
        if (!canWriteToConversation) return;
        playSendSound();
        void sendMessage(message, null, undefined);
    };

    const handleDeleteMessage = async (messageId: number) => {
        // Confirmation is handled once by MessageItem's shared ConfirmationModal.
        // Do not prompt again here with the browser-native confirm dialog.
        const previousMessage = messages.find(message => message.id === messageId);
        if (!previousMessage || previousMessage.isDeleted) return;
        setMessages(previous => previous.map(message => message.id === messageId ? { ...message, isDeleted: true } : message));
        try {
            const token = await currentUser?.getIdToken();
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/chat/message/${messageId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.message || "Failed to delete");

            toast.success("Message deleted");
        } catch (error: any) {
            setMessages(previous => previous.map(message => message.id === messageId ? { ...message, isDeleted: previousMessage.isDeleted } : message));
            toast.error(error.message || "Error deleting message.");
        }
    };

    const handleEditMessage = (messageId: number) => {
        const msgToEdit = messages.find(m => m.id === messageId);
        if (msgToEdit) {
            draftBeforeEditRef.current = { plaintext: commentText, doc: commentDoc };
            setEditingMessageId(messageId);
            setSelectedFile(undefined);
            setFilePreview(null);
            const extractDoc = (payload: any): any => {
                if (typeof payload === 'string') {
                    try {
                        let cleanStr = payload.trim();
                        if (cleanStr.startsWith('"') && cleanStr.endsWith('"')) {
                            cleanStr = cleanStr.slice(1, -1).replace(/\\"/g, '"');
                        }
                        return extractDoc(JSON.parse(cleanStr));
                    } catch { return null; }
                }
                if (typeof payload === 'object') {
                    if (payload.type === 'doc' && payload.content) return payload;
                    if (payload.doc) return extractDoc(payload.doc);
                }
                return null;
            };
            const extracted = extractDoc(msgToEdit.messageDoc || msgToEdit.messageContent);
            window.requestAnimationFrame(() => {
                if (extracted) chatInputRef.current?.setContent(extracted);
                else chatInputRef.current?.setContent(msgToEdit.messageContent);
            });
        }
    };

    return (
        <div className={`chat-theme-shell flex w-full h-full overflow-hidden relative ${isMeetingDark ? 'bg-slate-950 text-slate-100' : 'bg-white dark:bg-[#11131c] dark:text-slate-100'}`}>
            <div className={`chat-theme-body chat_body p-0 m-0 border-none flex flex-col h-full transition-all ${isMeetingDark ? 'bg-slate-950' : 'bg-white dark:bg-[#11131c]'} ${activeThreadMessage ? (singlePane ? 'hidden' : `hidden md:flex md:w-[60%] lg:w-[65%] border-r ${isMeetingDark ? 'border-slate-800' : 'border-slate-200 dark:border-white/[0.06]'}`) : 'w-full'}`}>
                <ChatHeader conversation={conversation} handleChatClose={handleChatClose} singlePane={singlePane} isShowCallingIcon={isShowCallingIcon && !isBotConversation} externalUserDetails={externalUserDetails} isSideView={isSideView} appearance={appearance} onOpenSearch={currentUser ? () => setUtilityPanel(previous => previous === "search" ? null : "search") : undefined} onOpenBookmarks={currentUser ? () => setUtilityPanel(previous => previous === "bookmarks" ? null : "bookmarks") : undefined} activeUtility={utilityPanel} />

                <div className={`flex-1 overflow-hidden relative ${isMeetingDark ? 'bg-slate-950' : 'bg-white dark:bg-[#11131c]'}`}>
                    {isLoading ? (
                        <div className="chat-loading-surface h-full overflow-hidden">
                            <ChatMessageSkeleton rows={singlePane ? 7 : 9} compact={singlePane} />
                        </div>
                    ) : messages.filter(m => m.parentMessageId == null).length === 0 ? (
                        <div className={`chat-empty-conversation flex h-full items-center justify-center px-5 py-8 ${isMeetingDark ? 'bg-slate-950' : 'bg-white dark:bg-[#11131c]'}`}>
                            <div className="flex w-full max-w-md flex-col items-center text-center">
                                <div className="relative mb-4">
                                    <div className={`chat-empty-icon flex h-16 w-16 items-center justify-center rounded-2xl border ${isMeetingDark ? 'border-slate-700 bg-slate-900 text-purple-300' : 'border-[#7E57C2]/15 bg-[#7E57C2]/10 text-[#7E57C2]'}`}>
                                        <MessageCircleMore size={29} strokeWidth={1.8} />
                                    </div>
                                    <div className={`chat-empty-sparkle absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-lg border shadow-sm ${isMeetingDark ? 'border-slate-700 bg-slate-900 text-purple-300' : 'border-purple-100 bg-white text-[#7E57C2]'}`}>
                                        <Sparkles size={13} strokeWidth={2} />
                                    </div>
                                </div>

                                <h3 className={`chat-empty-title m-0 text-base font-semibold ${isMeetingDark ? 'text-slate-100' : 'text-slate-900'}`}>
                                    This is the start of your conversation
                                </h3>
                                <p className={`chat-empty-copy mb-0 mt-1.5 max-w-sm text-xs leading-5 ${isMeetingDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                    Break the ice with a message below, or choose a quick hello to get things started.
                                </p>

                                {canWriteToConversation && (
                                    <div className="mt-5 flex max-w-sm flex-wrap items-center justify-center gap-2">
                                        {["Hi! 👋", "Hello! Hope you're doing well.", "Hey! Do you have a minute?"].map((suggestion) => (
                                            <button
                                                key={suggestion}
                                                type="button"
                                                onClick={() => handleQuickStart(suggestion)}
                                                disabled={isSending}
                                                className={`chat-quick-start rounded-full border px-3 py-1.5 text-xs font-medium transition-all disabled:cursor-not-allowed disabled:opacity-50 ${isMeetingDark ? 'chat-quick-start--dark border-slate-700 bg-slate-900 text-slate-300 hover:border-[#7E57C2]/60 hover:text-purple-200' : 'border-slate-200 bg-white text-slate-600 shadow-sm hover:border-[#7E57C2]/30 hover:bg-[#7E57C2]/5 hover:text-[#6847A3]'}`}
                                            >
                                                {suggestion}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <MessageList
                            conversationId={conversation.id}
                            messages={messages.filter(m => m.parentMessageId == null)}
                            currentUserId={currentUser ? currentUser.uid : externalUserDetails!.externalUserDetailId}
                            onAddReaction={sendReaction}
                            externalUserDetails={externalUserDetails}
                            onLoadMore={loadMoreMessages}
                            loadingTop={loadingMore}
                            hasMore={hasMore}
                            scrollToBottomTrigger={scrollToBottomTrigger}
                            onEditMessage={handleEditMessage}
                            onDeleteMessage={handleDeleteMessage}
                            onOpenThread={(msg) => {
                                threadOriginFocusRef.current = (document.activeElement as HTMLElement | null)?.closest?.("[data-chat-message-row]") as HTMLElement | null;
                                dismissedLinkedThreadRef.current = null;
                                setActiveThreadMessage(msg);
                            }}
                            onToggleBookmark={currentUser ? (messageId) => { void handleToggleBookmark(messageId).catch(() => undefined); } : undefined}
                            onRetryMessage={retryMessage}
                            onCancelMessage={(messageId) => { void cancelMessage(messageId); }}
                            compact={singlePane}
                            appearance={appearance}
                            focusedMessageId={!initialThreadRootId ? effectiveFocusedMessageId : undefined}
                            onReadThrough={(messageId) => handleReadThrough(messageId)}
                            readTrackingEnabled={isActiveView && !activeThreadMessage && !utilityPanel}
                        />
                    )}
                </div>

                <div className={`chat-composer-dock p-0 ${isMeetingDark ? 'bg-slate-950' : 'bg-white dark:bg-[#11131c]'}`}>
                    {!canWriteToConversation ? (
                        <div
                            role="status"
                            className={`chat-composer-readonly ${isMeetingDark ? 'chat-composer-readonly--dark' : ''} flex items-start gap-3 rounded-2xl border-[1px] px-4 py-3 ${isMeetingDark ? 'border-[#7e57c2]/30 bg-slate-900 text-purple-200' : 'border-[#7e57c2]/20 bg-[#7e57c2]/5 text-[#6847a3]'}`}
                        >
                            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#7e57c2]/10">
                                <LockKeyhole size={16} />
                            </div>
                            <div className="min-w-0">
                                <p className="chat-composer-readonly__title m-0 text-sm font-semibold">
                                    {readOnlyTitle}
                                </p>
                                <p className={`chat-composer-readonly__body mb-0 mt-1 text-xs leading-relaxed ${isMeetingDark ? 'text-slate-400' : 'text-slate-600'}`}>
                                    {readOnlyReason}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="relative flex h-auto w-full shrink-0 items-end">
                                {typingDisplayNames.length > 0 && !editingMessageId && (
                                    <div className={`chat-typing-indicator absolute -top-[22px] left-4 flex items-center gap-1.5 rounded-t-lg border-[1px] border-b-0 px-3 py-0.5 text-[11px] font-medium italic shadow-sm ${isMeetingDark ? 'border-slate-700 bg-slate-900 text-slate-400' : 'border-slate-200 bg-white text-slate-500'}`}>
                                        <span className="flex gap-0.5">
                                            <span className="h-1 w-1 animate-bounce rounded-full bg-indigo-400" />
                                            <span className="h-1 w-1 animate-bounce rounded-full bg-indigo-400 [animation-delay:-0.15s]" />
                                            <span className="h-1 w-1 animate-bounce rounded-full bg-indigo-400 [animation-delay:-0.3s]" />
                                        </span>
                                        <span>{typingDisplayNames.join(", ")} is typing...</span>
                                    </div>
                                )}

                                <div className={`chat-composer relative z-10 flex h-auto w-full flex-col rounded-2xl shadow-sm transition-all ${isMeetingDark ? 'bg-slate-900 focus-within:ring-2 focus-within:ring-[#7E57C2]/40' : 'bg-slate-100 focus-within:ring-2 focus-within:ring-[#7E57C2]/25 dark:bg-[#1d202b] dark:focus-within:ring-purple-400/30'}`}>
                                    {editingMessageId && (
                                        <div className={`chat-edit-banner flex items-center justify-between rounded-t-2xl border-b-[1px] px-4 py-2 ${isMeetingDark ? 'border-slate-700 bg-[#7E57C2]/15' : 'border-indigo-100 bg-indigo-50'}`}>
                                            <span className={`text-[11px] font-semibold uppercase tracking-wider ${isMeetingDark ? 'text-purple-200' : 'text-indigo-700'}`}>
                                                Editing Message...
                                            </span>
                                            <button
                                                onClick={cancelEdit}
                                                className={isMeetingDark ? "text-purple-300 hover:text-purple-100" : "text-indigo-400 hover:text-indigo-700"}
                                            >
                                                <X size={14} strokeWidth={2.5} />
                                            </button>
                                        </div>
                                    )}

                                    {selectedFile && (
                                        <div className={`chat-selected-file mx-2 mt-2 flex items-center gap-3 rounded-xl border-[1px] p-2 ${isMeetingDark ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-slate-50'}`}>
                                            <div className={`chat-selected-file-preview flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg border-[1px] ${isMeetingDark ? 'border-slate-700 bg-slate-900' : 'border-slate-200 bg-white'}`}>
                                                {selectedFile.type.startsWith("image/") && filePreview?.url ? (
                                                    <img
                                                        src={filePreview.url}
                                                        alt="Attachment preview"
                                                        className="h-full w-full object-cover"
                                                    />
                                                ) : (
                                                    <FileIcon size={18} className="text-slate-400" />
                                                )}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className={`m-0 truncate text-xs font-semibold ${isMeetingDark ? 'text-slate-200' : 'text-slate-700'}`}>
                                                    {selectedFile.name}
                                                </p>
                                                <p className={`m-0 mt-0.5 text-[10px] ${isMeetingDark ? 'text-slate-500' : 'text-slate-400'}`}>
                                                    {selectedFile.type.startsWith("image/") ? "Image attachment" : "File attachment"} · {formatFileSize(selectedFile.size)}
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={clearSelectedFile}
                                                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors ${isMeetingDark ? 'text-slate-500 hover:bg-slate-700 hover:text-red-300' : 'text-slate-400 hover:bg-white hover:text-red-500'}`}
                                                aria-label="Remove attachment"
                                            >
                                                <X size={14} strokeWidth={2.5} />
                                            </button>
                                        </div>
                                    )}

                                    <input
                                        id={`chatFileInput-${conversation.id}`}
                                        type="file"
                                        className="hidden"
                                        disabled={editingMessageId !== null}
                                        onChange={(event) => {
                                            const file = event.target.files?.[0];
                                            if (file) selectFile(file);
                                            event.currentTarget.value = "";
                                        }}
                                    />

                                    {/* Single Pane = true TEMP -- DO NOT REMOVE */}
                                    {true ? (
                                        <>
                                            {showComposerActions && (
                                                <div className={`chat-composer-actions flex items-center gap-1 px-2 py-1.5 ${isMeetingDark ? 'bg-slate-800/40' : ''}`}>
                                                    <Tooltip content={editingMessageId !== null ? "Cannot attach files while editing" : "Attach a file"}>
                                                        <label
                                                            htmlFor={`chatFileInput-${conversation.id}`}
                                                            className={`chat-composer-option flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
                                                                editingMessageId !== null
                                                                    ? "cursor-not-allowed text-slate-300"
                                                                    : "cursor-pointer text-slate-500 hover:bg-slate-100 hover:text-indigo-600"
                                                            }`}
                                                        >
                                                            <Paperclip size={17} />
                                                        </label>
                                                    </Tooltip>
                                                    <div className="relative">
                                                        <Tooltip content="Emoji">
                                                            <button
                                                                data-emoji-picker-trigger
                                                                type="button"
                                                                onClick={() => setShowEmojiPicker((previous) => !previous)}
                                                                className={`chat-composer-option flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
                                                                    showEmojiPicker ? "chat-composer-option-active bg-purple-50 text-[#7E57C2]" : "text-slate-500 hover:bg-slate-100 hover:text-[#7E57C2]"
                                                                }`}
                                                                aria-label="Choose an emoji"
                                                                aria-expanded={showEmojiPicker}
                                                            >
                                                                <Smile size={17} />
                                                            </button>
                                                        </Tooltip>
                                                        {showEmojiPicker && (
                                                            <div className={`chat-emoji-popover absolute bottom-[calc(100%+8px)] left-0 z-50 overflow-hidden rounded-2xl shadow-2xl ${isMeetingDark ? 'bg-slate-800' : 'bg-white'}`}>
                                                                <EmojiPicker
                                                                    autoFocusSearch
                                                                    onDismiss={() => setShowEmojiPicker(false)}
                                                                    onSelect={(emoji) => {
                                                                        chatInputRef.current?.insertEmoji(emoji);
                                                                        setShowEmojiPicker(false);
                                                                    }}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <Tooltip content={showFormatting ? "Hide formatting" : "Formatting"}>
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowFormatting((previous) => !previous)}
                                                            className={`chat-composer-option flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
                                                                showFormatting ? "chat-composer-option-active bg-purple-50 text-[#7E57C2]" : "text-slate-500 hover:bg-slate-100 hover:text-[#7E57C2]"
                                                            }`}
                                                        >
                                                            <Type size={17} />
                                                        </button>
                                                    </Tooltip>
                                                    <span className="ml-1 text-[11px] text-slate-400">Paste screenshots with Ctrl+V</span>
                                                </div>
                                            )}

                                            <div className="chat-composer-reference-previews">
                                                <ResourceLinkPreviews doc={commentDoc} appearance={isMeetingDark ? 'dark' : undefined} />
                                                <TaskFaqLinkPreviews doc={commentDoc} appearance={isMeetingDark ? 'dark' : undefined} />
                                            </div>
                                            <div className="relative flex min-h-[46px] items-center gap-1.5 px-2 py-1.5">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setShowComposerActions((previous) => !previous);
                                                        if (showComposerActions) {
                                                            setShowEmojiPicker(false);
                                                            setShowFormatting(false);
                                                        }
                                                    }}
                                                    className={`chat-composer-expand flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-all ${
                                                        showComposerActions
                                                            ? "rotate-45 bg-purple-50 text-[#7E57C2]"
                                                            : "bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                                                    }`}
                                                    aria-label={showComposerActions ? "Hide message options" : "Show message options"}
                                                >
                                                    <Plus size={18} />
                                                </button>

                                                <div className="min-w-0 flex-1">
                                                    <MessageEditor
                                                        key={`compact-chat-editor-${conversation.id}`}
                                                        ref={chatInputRef}
                                                        onUpdate={handleEditorUpdate}
                                                        onSendMessage={handleSubmit}
                                                        onPasteFile={selectFile}
                                                        users={conversation.participants ?? []}
                                                        compact
                                                        showFormatting={showFormatting}
                                                        canSendWithoutText={Boolean(selectedFile)}
                                                        appearance={appearance}
                                                        placeholder={composerPlaceholder}
                                                    />
                                                </div>

                                                <button
                                                    className={`chat-send-button flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-all ${
                                                        isSending || (isEditorEmpty && !selectedFile)
                                                            ? "chat-send-button-disabled cursor-not-allowed bg-slate-100 text-slate-300"
                                                            : "bg-[#7E57C2] text-white hover:bg-[#6F49B5]"
                                                    }`}
                                                    onClick={handleSubmit}
                                                    disabled={isSending || (isEditorEmpty && !selectedFile)}
                                                    aria-label={editingMessageId ? "Save message" : "Send message"}
                                                >
                                                    {isSending ? (
                                                        <Loader2 className="animate-spin" size={16} />
                                                    ) : editingMessageId ? (
                                                        <Check size={16} />
                                                    ) : (
                                                        <Send size={15} className="-ml-0.5" />
                                                    )}
                                                </button>

                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <MessageEditor
                                                key={`chat-editor-${conversation.id}`}
                                                ref={chatInputRef}
                                                onUpdate={handleEditorUpdate}
                                                onSendMessage={handleSubmit}
                                                onPasteFile={selectFile}
                                                users={conversation.participants ?? []}
                                                canSendWithoutText={Boolean(selectedFile)}
                                                appearance={appearance}
                                                placeholder={composerPlaceholder}
                                            />

                                            <div className={`flex shrink-0 items-center justify-between rounded-b-2xl border-t-[1px] px-3 py-2 ${isMeetingDark ? 'border-slate-700 bg-slate-800/80' : 'border-slate-100 bg-slate-50/80'}`}>
                                                <div className="flex items-center gap-1">
                                                    <Tooltip content={editingMessageId !== null ? "Cannot attach files while editing" : "Attach a file"}>
                                                        <label
                                                            htmlFor={`chatFileInput-${conversation.id}`}
                                                            className={`rounded-lg p-1.5 transition-all ${
                                                                editingMessageId !== null
                                                                    ? "cursor-not-allowed text-slate-300"
                                                                    : "cursor-pointer text-slate-400 hover:bg-indigo-100 hover:text-indigo-600"
                                                            }`}
                                                        >
                                                            <Paperclip size={18} />
                                                        </label>
                                                    </Tooltip>
                                                    <button
                                                        data-emoji-picker-trigger
                                                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                                        className={`rounded-lg p-1.5 transition-all ${showEmojiPicker ? "bg-indigo-100 text-indigo-600" : "text-slate-400"}`}
                                                    >
                                                        <Smile size={18} />
                                                    </button>
                                                </div>

                                                <button
                                                    className={`chat-send-button flex h-9 w-9 items-center justify-center rounded-full shadow-sm transition-all ${
                                                        isSending || (isEditorEmpty && !selectedFile)
                                                            ? "chat-send-button-disabled cursor-not-allowed bg-slate-200 text-slate-400"
                                                            : "bg-[#7E57C2] text-white hover:bg-[#6F49B5]"
                                                    }`}
                                                    onClick={handleSubmit}
                                                    disabled={isSending || (isEditorEmpty && !selectedFile)}
                                                >
                                                    {isSending ? <Loader2 className="animate-spin" size={18} /> : editingMessageId ? <Check size={18} /> : <Send size={16} className="-ml-0.5" />}
                                                </button>

                                                {showEmojiPicker && (
                                                    <div className={`chat-emoji-popover absolute bottom-12 left-0 z-50 overflow-hidden rounded-2xl shadow-2xl ${isMeetingDark ? 'bg-slate-800' : 'bg-white'}`}>
                                                        <EmojiPicker
                                                            autoFocusSearch
                                                            onDismiss={() => setShowEmojiPicker(false)}
                                                            onSelect={(emoji) => {
                                                                chatInputRef.current?.insertEmoji(emoji);
                                                                setShowEmojiPicker(false);
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            {!singlePane && (
                                <div className={`pt-1 text-right text-xs ${isMeetingDark ? 'text-slate-600' : 'text-gray-400'}`}>
                                    Shift + Enter to add a new line
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Sliding Thread Panel */}
            <div
                className={`chat-thread-panel-shell fixed md:absolute inset-y-0 right-0 z-20 shadow-2xl transition-transform duration-300 ease-in-out transform ${isMeetingDark ? 'bg-slate-950' : 'bg-white dark:bg-[#11131c]'} ${activeThreadMessage ? "translate-x-0" : "translate-x-full"
                    } ${singlePane ? 'w-full' : 'w-[40%] lg:w-[35%]'}`}
            >
                {activeThreadMessage && (
                    <ThreadPanel
                        readTrackingEnabled={isActiveView && !utilityPanel}
                        rootMessage={activeThreadMessage}
                        focusedMessageId={effectiveFocusedMessageId}
                        conversation={conversation}
                        onClose={closeActiveThread}
                        externalUserDetails={externalUserDetails}
                        canWrite={canWriteToConversation}
                        readOnlyReason={readOnlyReason}
                        readOnlyTitle={
                            isChannelArchived
                                ? "Archived channel"
                                : "Replies are read only"
                        }
                        appearance={appearance}
                        singlePane={singlePane}
                        onToggleBookmark={currentUser ? handleToggleBookmark : undefined}
                        onReadThrough={(messageId) => handleReadThrough(messageId, activeThreadMessage.id)}
                    />
                )}
            </div>

            {utilityPanel && currentUser && (
                <div className="absolute inset-0 z-30 shadow-2xl">
                    <ChatSearchPanel
                        conversation={conversation}
                        mode={utilityPanel}
                        onClose={() => setUtilityPanel(null)}
                        onOpenResult={(message) => void handleOpenSearchResult(message)}
                        onToggleBookmark={handleToggleBookmark}
                        appearance={appearance}
                    />
                </div>
            )}
        </div>
    );
};

export default GlobalChatWindow;
